import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useCallProtocol } from '../../hooks/useCallProtocol';
import { useCallerBookings } from '../../hooks/useCallerBookings';

// German labels for the protocol vocabulary. The keys come from the backend
// (PROTOCOL_CATEGORIES / PROTOCOL_OUTCOMES) and mirror the topics the AI phone agent records.
export const CATEGORY_LABELS = {
  bestehende_buchung: 'Bestehende Buchung',
  verschiebung: 'Verschiebung',
  stornierung: 'Stornierung',
  teilnehmerzahl: 'Teilnehmerzahl',
  pakete_extras: 'Pakete & Extras',
  raumwechsel: 'Raumwechsel',
  standortinfo: 'Standortinfo',
  oeffnungszeiten: 'Öffnungszeiten',
  neubuchung: 'Neubuchung',
  rauminfo: 'Rauminfo',
  preisauskunft: 'Preisauskunft',
  gutscheine: 'Gutscheine',
  flextickets: 'Flextickets',
  rueckruf: 'Rückruf',
  reklamation: 'Reklamation',
  sonstiges: 'Sonstiges',
};

export const OUTCOME_LABELS = {
  geloest: 'Gelöst',
  weitergeleitet: 'Weitergeleitet',
  rueckruf_vereinbart: 'Rückruf vereinbart',
  offen: 'Offen',
  kein_anliegen: 'Kein Anliegen',
};

const SOURCE_LABELS = {
  direct: 'Direktanruf',
  ai_transfer: 'Vom KI-Agenten übergeben',
  manual: 'Manueller Eintrag',
};

export const labelFor = (map, key) => map[key] || key;

const EMPTY_FORM = {
  category: '',
  outcome: '',
  followUpRequired: false,
  followUpNote: '',
  followUpDueAt: '',
  note: '',
  bookingNumber: '',
  caller: '',
};

const formatSeconds = seconds => {
  if (seconds === null || seconds === undefined) return '—';
  const total = Number(seconds);
  if (!Number.isFinite(total)) return '—';
  const min = Math.floor(total / 60);
  const sec = Math.round(total % 60);
  return min > 0 ? `${min}:${String(sec).padStart(2, '0')} min` : `${sec} sek`;
};

// A labelled fact in the context column.
const Fact = ({ label, value }) => (
  <Stack direction='row' justifyContent='space-between' spacing={2}>
    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography variant='body2' sx={{ fontWeight: 500, textAlign: 'right' }}>
      {value ?? '—'}
    </Typography>
  </Stack>
);

const SectionTitle = ({ children }) => (
  <Typography variant='subtitle2' sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
    {children}
  </Typography>
);

// The protocol workspace, shared by the popup that opens on a ringing call and the protocol list.
// Laid out as a full-height two-column view: everything known about the caller on the left, the
// protocol the employee writes on the right — so notes can be taken without leaving the dialog.
export default function CallProtocolDialog({
  open,
  onClose,
  protocol,
  aiContext,
  callContext,
  bookingContext,
  caller,
  onSaved,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { getOptions, updateProtocol } = useCallProtocol();

  const [options, setOptions] = useState({ categories: [], outcomes: [] });
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  // What the referenced booking number resolved to. Seeded from the loaded protocol and refreshed
  // from every save response, so a typo shows up as "nicht gefunden" instead of silently vanishing.
  const [linkedBooking, setLinkedBooking] = useState(null);

  const callerNumber = caller || protocol?.caller || callContext?.caller || null;
  const { bookings, isLoading: bookingsLoading, canSearch } = useCallerBookings(open ? callerNumber : null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getOptions()
      .then(res => {
        if (!cancelled && res) {
          setOptions({ categories: res.categories || [], outcomes: res.outcomes || [] });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, getOptions]);

  useEffect(() => {
    if (!protocol) return;
    setForm({
      category: protocol.category || '',
      outcome: protocol.outcome || '',
      followUpRequired: !!protocol.followUpRequired,
      followUpNote: protocol.followUpNote || '',
      followUpDueAt: protocol.followUpDueAt ? dayjs(protocol.followUpDueAt).format('YYYY-MM-DD') : '',
      note: protocol.note || '',
      bookingNumber: protocol.bookingNumber || '',
      caller: protocol.caller || '',
    });
  }, [protocol]);

  useEffect(() => {
    setLinkedBooking(bookingContext || null);
  }, [bookingContext]);

  const isManual = protocol?.source === 'manual';

  // A number that was saved but matched nothing is a typo the employee should see.
  const bookingUnresolved = !!linkedBooking && linkedBooking.found === false;
  const bookingHelper = useMemo(() => {
    if (bookingUnresolved) return 'Zu dieser Nummer wurde keine Buchung gefunden.';
    if (linkedBooking?.found) {
      return [
        linkedBooking.customerName,
        linkedBooking.startTime ? dayjs(linkedBooking.startTime).format('DD.MM.YYYY HH:mm') : null,
        linkedBooking.branchName,
      ]
        .filter(Boolean)
        .join(' · ');
    }
    return 'Buchung, um die es im Gespräch ging — links anklickbar oder von Hand eintragen.';
  }, [linkedBooking, bookingUnresolved]);

  const headline = useMemo(() => {
    if (callerNumber) return callerNumber;
    return isManual ? 'Manueller Eintrag' : 'Eingehender Anruf';
  }, [callerNumber, protocol, isManual]);

  const save = async status => {
    if (!protocol?._id) return;
    setSaving(true);
    try {
      const payload = { ...form, followUpDueAt: form.followUpDueAt || null, status };
      // The caller of a real call comes from Comdesk and must not be overwritten.
      if (!isManual) delete payload.caller;
      const result = await updateProtocol(protocol._id, payload);
      setLinkedBooking(result?.bookingContext || null);
      toast.success(
        status === 'completed' ? 'Gesprächsprotokoll abgeschlossen' : 'Gesprächsprotokoll gespeichert'
      );
      if (onSaved) onSaved();
      onClose();
    } catch {
      // useCallProtocol already surfaced the error.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth='xl'
      fullWidth
      PaperProps={{ sx: { height: fullScreen ? '100%' : '92vh', maxHeight: '92vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' alignItems={{ sm: 'center' }} spacing={1}>
          <Box>
            <Typography variant='h5' component='span' sx={{ fontWeight: 700 }}>
              {headline}
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              Gesprächsprotokoll
            </Typography>
          </Box>
          <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
            <Chip
              size='small'
              color={protocol?.source === 'ai_transfer' ? 'info' : 'default'}
              label={labelFor(SOURCE_LABELS, protocol?.source || 'direct')}
            />
            <Chip
              size='small'
              color={protocol?.status === 'completed' ? 'success' : 'warning'}
              label={protocol?.status === 'completed' ? 'Abgeschlossen' : 'Entwurf'}
            />
            {protocol?.meta?.createdAt && (
              <Chip size='small' variant='outlined' label={dayjs(protocol.meta.createdAt).format('DD.MM.YYYY HH:mm')} />
            )}
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(300px, 4fr) 6fr' },
            height: '100%',
          }}
        >
          {/* Context column — everything already known, so the agent does not have to look it up */}
          <Box
            sx={{
              p: 2.5,
              borderRight: { md: 1 },
              borderBottom: { xs: 1, md: 0 },
              borderColor: { xs: 'divider', md: 'divider' },
              bgcolor: 'action.hover',
              overflowY: 'auto',
            }}
          >
            <SectionTitle>Anruf</SectionTitle>
            <Stack spacing={0.75} sx={{ mb: 3 }}>
              <Fact label='Nummer' value={callerNumber} />
              <Fact
                label='Eingegangen'
                value={callContext?.startedAt ? dayjs(callContext.startedAt).format('DD.MM.YYYY HH:mm') : '—'}
              />
              <Fact label='Dauer' value={callContext ? formatSeconds(callContext.durationSeconds) : '—'} />
              <Fact label='Standort' value={callContext?.branchName} />
              <Fact label='Bereich' value={callContext?.entertainmentName} />
              <Fact label='Mitarbeiter' value={protocol?.agentName} />
            </Stack>

            {protocol?.source === 'ai_transfer' && (
              <>
                <SectionTitle>KI-Agent</SectionTitle>
                <Stack spacing={1} sx={{ mb: 3 }}>
                  {aiContext?.topics?.length > 0 && (
                    <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
                      {aiContext.topics.map(topic => (
                        <Chip key={topic} size='small' label={labelFor(CATEGORY_LABELS, topic)} />
                      ))}
                    </Stack>
                  )}
                  <Fact label='Gesprächsdauer KI' value={formatSeconds(aiContext?.durationSeconds)} />
                  {aiContext?.sentiment && <Fact label='Stimmung' value={aiContext.sentiment} />}
                  {aiContext?.bookingOutcome?.reservationCreated && (
                    <Typography variant='body2'>Die KI hat bereits eine Reservierung angelegt.</Typography>
                  )}
                  {aiContext?.bookingOutcome?.linkSent && (
                    <Typography variant='body2'>Der KI-Buchungslink wurde per SMS verschickt.</Typography>
                  )}
                  {aiContext?.summary ? (
                    <Paper variant='outlined' sx={{ p: 1.5 }}>
                      <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                        {aiContext.summary}
                      </Typography>
                    </Paper>
                  ) : (
                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                      Die KI-Zusammenfassung wird erst nach dem Gespräch ausgewertet und erscheint
                      hier später.
                    </Typography>
                  )}
                </Stack>
              </>
            )}

            {canSearch && callerNumber && (
              <>
                <SectionTitle>Buchungen dieser Nummer</SectionTitle>
                {bookingsLoading && (
                  <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                    Wird gesucht ...
                  </Typography>
                )}
                {!bookingsLoading && bookings.length === 0 && (
                  <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                    Keine Buchungen zu dieser Nummer gefunden.
                  </Typography>
                )}
                <Stack spacing={1}>
                  {bookings.map(booking => (
                    <Paper
                      key={booking._id}
                      variant='outlined'
                      sx={{ p: 1.25, cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                      onClick={() => setForm(prev => ({ ...prev, bookingNumber: booking.bookingNumber }))}
                      title='Als Buchung des Gesprächs übernehmen'
                    >
                      <Stack direction='row' justifyContent='space-between' spacing={1}>
                        <Typography variant='body2' sx={{ fontWeight: 600 }}>
                          {booking.bookingNumber}
                        </Typography>
                        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                          {booking.startTime ? dayjs(booking.startTime).format('DD.MM.YYYY HH:mm') : ''}
                        </Typography>
                      </Stack>
                      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                        {[booking.firstName, booking.lastName].filter(Boolean).join(' ') || '—'}
                        {booking.status ? ` · ${booking.status}` : ''}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </>
            )}
          </Box>

          {/* Protocol column */}
          <Box sx={{ p: 2.5, overflowY: 'auto' }}>
            <Stack spacing={2.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {isManual && (
                  <TextField
                    label='Anrufer / Kontakt'
                    defaultValue={form.caller}
                    onBlur={e => setForm(prev => ({ ...prev, caller: e.target.value }))}
                    fullWidth
                  />
                )}
                <TextField
                  label='Buchungsnummer'
                  // Controlled here on purpose: clicking a booking on the left fills this field.
                  value={form.bookingNumber}
                  onChange={e => setForm(prev => ({ ...prev, bookingNumber: e.target.value }))}
                  fullWidth
                  helperText={bookingHelper}
                  error={bookingUnresolved}
                  InputProps={{
                    endAdornment: form.bookingNumber ? (
                      <InputAdornment position='end'>
                        <Tooltip title='Buchung in neuem Tab öffnen'>
                          <IconButton
                            size='small'
                            edge='end'
                            onClick={() =>
                              window.open(
                                `/internalBookings?bookingNumber=${encodeURIComponent(form.bookingNumber)}`,
                                '_blank'
                              )
                            }
                          >
                            <OpenInNewIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  label='Anliegen'
                  value={form.category}
                  onChange={e => setForm(prev => ({ ...prev, category: e.target.value }))}
                  fullWidth
                  helperText='Worum ging es im Gespräch?'
                >
                  {options.categories.map(key => (
                    <MenuItem key={key} value={key}>
                      {labelFor(CATEGORY_LABELS, key)}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label='Ergebnis'
                  value={form.outcome}
                  onChange={e => setForm(prev => ({ ...prev, outcome: e.target.value }))}
                  fullWidth
                  helperText='Wie ist das Gespräch ausgegangen?'
                >
                  {options.outcomes.map(key => (
                    <MenuItem key={key} value={key}>
                      {labelFor(OUTCOME_LABELS, key)}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <TextField
                label='Gesprächsnotiz'
                multiline
                minRows={12}
                defaultValue={form.note}
                onBlur={e => setForm(prev => ({ ...prev, note: e.target.value }))}
                fullWidth
                placeholder='Was wurde besprochen, was wurde zugesagt?'
              />

              <Divider />

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.followUpRequired}
                      onChange={e => setForm(prev => ({ ...prev, followUpRequired: e.target.checked }))}
                    />
                  }
                  label='Wiedervorlage nötig'
                />

                {form.followUpRequired && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                    <TextField
                      label='Fällig am'
                      type='date'
                      value={form.followUpDueAt}
                      onChange={e => setForm(prev => ({ ...prev, followUpDueAt: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 200 }}
                    />
                    <TextField
                      label='Was ist zu tun?'
                      multiline
                      minRows={2}
                      defaultValue={form.followUpNote}
                      onBlur={e => setForm(prev => ({ ...prev, followUpNote: e.target.value }))}
                      fullWidth
                    />
                  </Stack>
                )}
              </Box>
            </Stack>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant='caption' sx={{ color: 'text.secondary', flexGrow: 1 }}>
          {protocol?.meta?.editedAt
            ? `Zuletzt gespeichert ${dayjs(protocol.meta.editedAt).format('DD.MM.YYYY HH:mm')}`
            : 'Noch nicht gespeichert'}
        </Typography>
        <Button onClick={onClose} disabled={saving}>
          Später
        </Button>
        <Button onClick={() => save('draft')} disabled={saving}>
          Zwischenspeichern
        </Button>
        <Button variant='contained' size='large' onClick={() => save('completed')} disabled={saving}>
          Abschliessen
        </Button>
      </DialogActions>
    </Dialog>
  );
}
